import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@shared-ui/sheet";
import { Skeleton } from "@shared-ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared-ui/tabs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { DataTable } from "@/features/catalog/components/data-table";
import { DecisionNotePanel } from "../components/decision-note-panel";
import {
	PortfolioReviewQueue,
	usePendingPortfolios,
} from "../components/portfolio-review-queue";
import { QueueLoadError } from "../components/queue-load-error";
import { RealtorReviewDetails } from "../components/realtor-review-details";
import { PENDING_QUEUE_QUERY_OPTIONS } from "../pending-queue-options";
import {
	type ApprovalType,
	approveProfile,
	listPendingProfiles,
	type ReviewableProfile,
	type ReviewableRealtorProfile,
	rejectProfile,
} from "../services/approvals-api.service";

/*
 * The profile review queues, in tab order. The Portfolios tab follows them and
 * is rendered by `PortfolioReviewQueue`, because portfolios are not profiles.
 *
 * All three backend queues answer the same shape, so the table below is
 * rendered once and driven from this list. The designer and contractor bodies
 * used to be byte-identical copies; a third copy for realtors is what this
 * avoids.
 */
const QUEUES = [
	{
		type: "designers",
		label: "Designers",
		empty: "No designer profiles are waiting for review.",
	},
	{
		type: "contractors",
		label: "Contractors",
		empty: "No contractor profiles are waiting for review.",
	},
	{
		type: "realtors",
		label: "Realtors",
		empty: "No realtor profiles are waiting for review.",
	},
] as const satisfies ReadonlyArray<{
	type: ApprovalType;
	label: string;
	empty: string;
}>;

/**
 * A queue's count for its card: "—" when the queue failed to load, so a
 * failure does not read as an empty queue.
 */
function queueCount(query: {
	data?: readonly unknown[];
	isError: boolean;
}): number | string {
	if (query.isError && !query.data) return "—";
	return query.data?.length ?? 0;
}

function ReviewQueueTable({
	profiles,
	isLoading,
	isError,
	isRetrying,
	onRetry,
	emptyTitle,
	onSelect,
}: {
	profiles: ReviewableProfile[] | undefined;
	isLoading: boolean;
	isError: boolean;
	isRetrying: boolean;
	onRetry: () => void;
	emptyTitle: string;
	onSelect: (profile: ReviewableProfile) => void;
}) {
	if (isLoading) {
		return <Skeleton className="h-64 w-full rounded-2xl" />;
	}

	if (isError && !profiles) {
		return <QueueLoadError onRetry={onRetry} isRetrying={isRetrying} />;
	}

	if (!profiles?.length) {
		return <EmptyState title={emptyTitle} />;
	}

	return (
		<DataTable
			rows={profiles}
			rowKey={(profile) => profile.id}
			onRowActivate={onSelect}
			rowLabel={(profile) => `Review ${profile.name}`}
			columns={[
				{
					header: "Name",
					cell: (profile) => profile.name,
					cellClassName: "font-medium",
				},
				{
					header: "Created",
					cell: (profile) => new Date(profile.createdAt).toLocaleDateString(),
					cellClassName: "text-muted-foreground",
				},
				{
					header: "Status",
					cell: (profile) => (
						<Badge variant="warning">{profile.reviewStatus}</Badge>
					),
				},
			]}
		/>
	);
}

/** A row from the realtors queue, which answers full realtor profiles. */
function isRealtorRow(
	type: ApprovalType,
	_profile: ReviewableProfile,
): _profile is ReviewableRealtorProfile {
	return type === "realtors";
}

/** The profile queues, then the portfolio queue, which has its own endpoints and types. */
type ApprovalTab = ApprovalType | "portfolios";

export function ApprovalsDashboardPage() {
	const queryClient = useQueryClient();
	const [tab, setTab] = useState<ApprovalTab>("designers");
	const [selectedProfile, setSelectedProfile] =
		useState<ReviewableProfile | null>(null);
	const [selectedType, setSelectedType] = useState<ApprovalType>("designers");
	const [notes, setNotes] = useState("");

	const designersQuery = useQuery({
		queryKey: ["approvals", "designers"],
		queryFn: () => listPendingProfiles("designers"),
		...PENDING_QUEUE_QUERY_OPTIONS,
	});

	const contractorsQuery = useQuery({
		queryKey: ["approvals", "contractors"],
		queryFn: () => listPendingProfiles("contractors"),
		...PENDING_QUEUE_QUERY_OPTIONS,
	});

	const realtorsQuery = useQuery({
		queryKey: ["approvals", "realtors"],
		queryFn: () => listPendingProfiles("realtors"),
		...PENDING_QUEUE_QUERY_OPTIONS,
	});

	const portfoliosQuery = usePendingPortfolios();

	const queryByType = {
		designers: designersQuery,
		contractors: contractorsQuery,
		realtors: realtorsQuery,
	} as const;

	const approveMutation = useMutation({
		mutationFn: (input: { type: ApprovalType; id: string; notes?: string }) =>
			approveProfile(input.type, input.id, input.notes),
		onSuccess: async (_profile, input) => {
			await queryClient.invalidateQueries({
				queryKey: ["approvals", input.type],
			});
			toast.success("Profile approved");
			setSelectedProfile(null);
			setNotes("");
		},
	});

	const rejectMutation = useMutation({
		mutationFn: (input: { type: ApprovalType; id: string; reason: string }) =>
			rejectProfile(input.type, input.id, input.reason),
		onSuccess: async (_profile, input) => {
			await queryClient.invalidateQueries({
				queryKey: ["approvals", input.type],
			});
			toast.success("Profile rejected");
			setSelectedProfile(null);
			setNotes("");
		},
	});

	const totals = {
		designers: queueCount(designersQuery),
		contractors: queueCount(contractorsQuery),
		realtors: queueCount(realtorsQuery),
	};
	const portfolioTotal = queueCount(portfoliosQuery);

	return (
		<div className="grid gap-5">
			<div className="grid gap-2">
				<p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
					Approvals
				</p>
				<div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
					<div>
						<h2 className="text-2xl font-semibold tracking-tight">
							Pending review
						</h2>
						<p className="text-sm text-muted-foreground">
							Review new designer, contractor and realtor profiles and submitted
							designer portfolios without deleting their record on rejection.
						</p>
					</div>
					<div className="flex gap-3">
						{QUEUES.map((queue) => (
							<Card
								key={queue.type}
								className="min-w-36 border-slate-200 shadow-none"
							>
								<CardContent className="p-4">
									<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
										{queue.label}
									</p>
									<p className="mt-2 text-2xl font-semibold">
										{totals[queue.type]}
									</p>
								</CardContent>
							</Card>
						))}
						<Card className="min-w-36 border-slate-200 shadow-none">
							<CardContent className="p-4">
								<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
									Portfolios
								</p>
								<p className="mt-2 text-2xl font-semibold">{portfolioTotal}</p>
							</CardContent>
						</Card>
					</div>
				</div>
			</div>

			<Card className="border-slate-200 shadow-none">
				<CardHeader>
					<CardTitle>Review queue</CardTitle>
					<CardDescription>
						Click a row to inspect its content and record a reasoned decision.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Tabs
						value={tab}
						onValueChange={(value) => setTab(value as ApprovalTab)}
					>
						<TabsList className="mb-4">
							{QUEUES.map((queue) => (
								<TabsTrigger key={queue.type} value={queue.type}>
									{queue.label}
								</TabsTrigger>
							))}
							<TabsTrigger value="portfolios">Portfolios</TabsTrigger>
						</TabsList>
						{QUEUES.map((queue) => (
							<TabsContent key={queue.type} value={queue.type} className="mt-0">
								<ReviewQueueTable
									profiles={queryByType[queue.type].data}
									isLoading={queryByType[queue.type].isLoading}
									isError={queryByType[queue.type].isError}
									isRetrying={queryByType[queue.type].isFetching}
									onRetry={() => void queryByType[queue.type].refetch()}
									emptyTitle={queue.empty}
									onSelect={(profile) => {
										setSelectedProfile(profile);
										setSelectedType(queue.type);
										setNotes(profile.reviewNotes || "");
									}}
								/>
							</TabsContent>
						))}
						<TabsContent value="portfolios" className="mt-0">
							<PortfolioReviewQueue />
						</TabsContent>
					</Tabs>
				</CardContent>
			</Card>

			<Sheet
				open={!!selectedProfile}
				onOpenChange={(open) => !open && setSelectedProfile(null)}
			>
				<SheetContent className="w-full overflow-y-auto sm:max-w-xl">
					{selectedProfile ? (
						<div className="grid gap-5">
							<SheetHeader>
								<SheetTitle>{selectedProfile.name}</SheetTitle>
							</SheetHeader>
							<Card className="border-slate-200 shadow-none">
								<CardHeader>
									<CardTitle>Profile context</CardTitle>
									<CardDescription>
										The current v1 review action is persistent and audit-backed.
									</CardDescription>
								</CardHeader>
								<CardContent className="grid gap-4 text-sm">
									<div className="grid gap-1">
										<span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
											Applicant
										</span>
										<span className="font-medium">{selectedProfile.name}</span>
									</div>
									<div className="grid gap-1">
										<span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
											Submitted
										</span>
										<span>
											{new Date(selectedProfile.createdAt).toLocaleString()}
										</span>
									</div>
									{selectedProfile.bio ? (
										<div className="grid gap-1">
											<span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
												Bio
											</span>
											<p className="rounded-2xl bg-slate-50 p-3 leading-6 text-slate-700">
												{selectedProfile.bio}
											</p>
										</div>
									) : null}
								</CardContent>
							</Card>
							{isRealtorRow(selectedType, selectedProfile) ? (
								<RealtorReviewDetails
									key={selectedProfile.id}
									profile={selectedProfile}
								/>
							) : null}
							<DecisionNotePanel
								description="Used as approval notes or rejection reason."
								placeholder="Record why this profile is approved or rejected."
								notes={notes}
								onNotesChange={setNotes}
								onApprove={() =>
									approveMutation.mutate({
										type: selectedType,
										id: selectedProfile.id,
										notes,
									})
								}
								onReject={() =>
									rejectMutation.mutate({
										type: selectedType,
										id: selectedProfile.id,
										reason: notes,
									})
								}
								isApproving={approveMutation.isPending}
								isRejecting={rejectMutation.isPending}
							/>
						</div>
					) : null}
				</SheetContent>
			</Sheet>
		</div>
	);
}
